"use client";

import IconBox from "@/components/common/IconBox";
import Button from "@/components/ui/Button";
import { regulatoryFormsData } from "@/data/regulatoryFormsData";
import { createSlug } from "@/utils/createSlug";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { GrResources } from "react-icons/gr";

export default function RegulatorySection() {
  const router = useRouter();

  const handleButtonClick = (item: (typeof regulatoryFormsData)[0]) => {
    if (item.type === "single" && item.href) {
      window.open(item.href, "_blank", "noopener,noreferrer");
    } else {
      // Navigate to regulatory page with hash for scroll and accordion opening
      router.push(`/regulatory-resources#${createSlug(item.title)}`);
    }
  };

  return (
    <section className="relative flex w-full items-center justify-center bg-linear-to-r from-[#D92319] to-[#8C0900] px-5 py-50 md:px-20">
      <div className="flex w-full max-w-[1000px] flex-col items-center justify-center text-center">
        {/* Icon */}
        <div className="mb-2.5">
          <IconBox icon={GrResources} />
        </div>

        {/* Title */}
        <h2 className="font-raleway mb-2.5 text-2xl font-bold text-white md:text-[2rem]">
          Regulatory Resources
        </h2>

        {/* Subtitle */}
        <p className="font-raleway mb-5 text-base font-normal text-white">
          We provide everything you need to start setting up your COAMs!
        </p>

        {/* Buttons Grid */}
        <div className="mb-10 grid grid-cols-1 gap-2.5 md:flex md:flex-wrap md:justify-center md:gap-5">
          {regulatoryFormsData.map((item, index) => (
            <motion.button
              key={index}
              initial="initial"
              whileHover="hover"
              onClick={() => handleButtonClick(item)}
              className="cursor-pointer rounded-full border border-white/50 px-5 py-2.5 text-[14px] text-white transition-colors md:px-6 md:py-3 md:text-base"
              variants={{
                initial: {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  boxShadow: "0 0 0 0px rgba(255, 255, 255, 0)",
                },
                hover: {
                  borderColor: "rgba(255, 255, 255, 1)",
                  boxShadow: "0 0 0 1px rgba(255, 255, 255, 1)",
                },
              }}
              transition={{ duration: 0.4, ease: [0.56, 0.51, 0.85, 0.84] }}
            >
              <div className="relative overflow-hidden">
                <motion.span
                  className="font-raleway block text-base text-white/80"
                  variants={{
                    initial: { y: "0%" },
                    hover: { y: "-100%" },
                  }}
                  transition={{ duration: 0.4, ease: [0.56, 0.51, 0.85, 0.84] }}
                >
                  {item.title}
                </motion.span>
                <motion.span
                  className="font-raleway absolute inset-0 block text-base"
                  variants={{
                    initial: { y: "100%" },
                    hover: { y: "0%" },
                  }}
                  transition={{ duration: 0.4, ease: [0.56, 0.51, 0.85, 0.84] }}
                >
                  {item.title}
                </motion.span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Learn More Button */}
        <Link
          href="/regulatory-resources"
          title="Learn More About Regulatory Resources"
          className="inline-block"
        >
          <Button variant="white">
            Learn More
            <span className="sr-only"> About Regulatory Resources</span>
          </Button>
        </Link>
      </div>
    </section>
  );
}
